import type { ReactNode } from "react";
import type { CrewKey } from "./crewData";

/**
 * Twelve tradespeople drawn only in the site palette (foreground navy,
 * primary blue, accent, background) plus three skin tones, so the figures sit
 * inside the design system instead of next to it. A trade is told apart by its
 * tool and headgear, never by an off-palette colour.
 */


const SKIN = ["#F2CBA6", "#D9A27A", "#8D5A3B"] as const;

type Headgear = "helmet" | "cap" | "capBack" | "paperHat" | "hair";

interface FigureSpec {
  skin: string;
  shirt: string;
  pants: string;
  head: Headgear;
  headClass: string;
  over?: ReactNode;
  back?: ReactNode;
  front?: ReactNode;
  face?: ReactNode;
}

const NAVY = "fill-foreground";
const BLUE = "fill-primary";
const BLUE_DARK = "fill-primary-hover";
const LIGHT = "fill-primary/25";
const PALE = "fill-accent";
const WHITE = "fill-background";
const STEEL = "fill-muted-foreground/40";

function Headwear({ kind, cls }: { kind: Headgear; cls: string }) {
  switch (kind) {
    case "helmet":
      return (
        <>
          <path d="M46 48a24 24 0 0 1 48 0z" className={cls} />
          <rect x="42" y="44" width="56" height="7" rx="3.5" className={cls} opacity="0.85" />
        </>
      );
    case "cap":
      return (
        <>
          <path d="M47 46q2-20 23-20q21 0 23 20z" className={cls} />
          <path d="M70 44h32q2 6-6 6H70z" className={cls} opacity="0.8" />
        </>
      );
    case "capBack":
      return (
        <>
          <path d="M47 46q2-20 23-20q21 0 23 20z" className={cls} />
          <path d="M47 44H32q0 6 8 6h7z" className={cls} opacity="0.8" />
        </>
      );
    case "paperHat":
      return <path d="M44 42l26-20 26 20z" className={cls} stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />;
    default:
      return <path d="M48 46q0-22 22-22t22 22q-8-10-22-10t-22 10z" className={cls} />;
  }
}

/** One standing figure; `back` paints behind the body, `front` in front of it. */
function Figure({ spec }: { spec: FigureSpec }) {
  return (
    <svg viewBox="0 0 140 230" aria-hidden="true" className="crew-figure text-foreground">
      {spec.back}
      <rect x="50" y="140" width="17" height="76" rx="6" className={spec.pants} />
      <rect x="73" y="140" width="17" height="76" rx="6" className={spec.pants} />
      <rect x="44" y="210" width="26" height="14" rx="6" className={NAVY} />
      <rect x="70" y="210" width="26" height="14" rx="6" className={NAVY} />
      <rect x="42" y="76" width="56" height="74" rx="16" className={spec.shirt} />
      <rect x="28" y="82" width="16" height="56" rx="8" className={spec.shirt} />
      <rect x="96" y="82" width="16" height="50" rx="8" className={spec.shirt} />
      {spec.over}
      <circle cx="36" cy="140" r="8" fill={spec.skin} />
      <circle cx="104" cy="134" r="8" fill={spec.skin} />
      <rect x="62" y="66" width="16" height="14" fill={spec.skin} />
      <circle cx="70" cy="52" r="22" fill={spec.skin} />
      <Headwear kind={spec.head} cls={spec.headClass} />
      {spec.face ?? (
        <>
          <circle cx="62" cy="56" r="2.6" className={NAVY} />
          <circle cx="78" cy="56" r="2.6" className={NAVY} />
          <path d="M63 66q7 3 14 0" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" />
        </>
      )}
      {spec.front}
    </svg>
  );
}

const vest = (
  <>
    <path d="M48 78h44v58H48z" className={BLUE} />
    <path d="M48 110h44v5H48zM48 124h44v5H48z" className={WHITE} />
  </>
);

const SPECS: Record<CrewKey, FigureSpec> = {
  mason: {
    skin: SKIN[1], shirt: NAVY, pants: NAVY, head: "helmet", headClass: WHITE,
    over: vest,
    back: (
      <g className={LIGHT}>
        <rect x="96" y="196" width="34" height="14" rx="2" />
        <rect x="104" y="182" width="30" height="14" rx="2" />
        <rect x="92" y="210" width="40" height="14" rx="2" />
      </g>
    ),
    front: (
      <>
        <path d="M104 134l22 10-4 10-18-14z" className={STEEL} />
        <rect x="100" y="128" width="6" height="12" rx="2" className={NAVY} />
      </>
    ),
  },
  carpenter: {
    skin: SKIN[0], shirt: BLUE, pants: NAVY, head: "hair", headClass: NAVY,
    back: <rect x="4" y="66" width="132" height="11" rx="3" className={LIGHT} transform="rotate(-10 70 72)" />,
    over: <path d="M42 100h56M42 122h56M58 76v74M82 76v74" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />,
    face: (
      <>
        <circle cx="62" cy="54" r="2.6" className={NAVY} />
        <circle cx="78" cy="54" r="2.6" className={NAVY} />
        <path d="M52 60q0 20 18 20t18-20q-6 8-18 8t-18-8z" className={NAVY} />
        <rect x="88" y="34" width="4" height="20" rx="1" className={BLUE} transform="rotate(20 90 44)" />
      </>
    ),
  },
  electrician: {
    skin: SKIN[2], shirt: NAVY, pants: NAVY, head: "helmet", headClass: BLUE,
    over: <path d="M74 90l-12 20h9l-5 16 14-22h-9l5-14z" className={WHITE} />,
    front: (
      <>
        <circle cx="112" cy="150" r="16" fill="none" className="stroke-primary" strokeWidth="5" />
        <circle cx="112" cy="150" r="8" fill="none" className="stroke-primary" strokeWidth="4" />
      </>
    ),
  },
  plumber: {
    skin: SKIN[0], shirt: WHITE, pants: BLUE, head: "cap", headClass: NAVY,
    over: (
      <>
        <path d="M46 98h48v52H46z" className={BLUE} />
        <path d="M52 76v26M88 76v26" className="stroke-primary" strokeWidth="7" />
      </>
    ),
    front: <rect x="102" y="122" width="9" height="56" rx="4" className={STEEL} transform="rotate(14 106 150)" />,
  },
  painter: {
    skin: SKIN[2], shirt: WHITE, pants: WHITE, head: "paperHat", headClass: WHITE,
    back: (
      <>
        <rect x="106" y="20" width="6" height="128" rx="3" className={NAVY} />
        <rect x="94" y="8" width="32" height="18" rx="6" className={BLUE} />
      </>
    ),
    over: (
      <g className={BLUE}>
        <circle cx="56" cy="100" r="4" />
        <circle cx="82" cy="120" r="5" opacity="0.6" />
        <circle cx="62" cy="138" r="3" />
        <circle cx="58" cy="182" r="4" opacity="0.6" />
      </g>
    ),
  },
  roofer: {
    skin: SKIN[1], shirt: BLUE_DARK, pants: NAVY, head: "helmet", headClass: NAVY,
    over: <path d="M50 78l40 66M90 78l-40 66" className="stroke-foreground" strokeWidth="5" />,
    front: (
      <>
        <rect x="110" y="104" width="7" height="32" rx="3" className={NAVY} />
        <rect x="102" y="100" width="24" height="10" rx="3" className={STEEL} />
      </>
    ),
  },
  service: {
    skin: SKIN[1], shirt: NAVY, pants: NAVY, head: "hair", headClass: NAVY,
    back: (
      <>
        <rect x="6" y="190" width="40" height="30" rx="4" className={BLUE} />
        <rect x="18" y="182" width="16" height="10" rx="3" fill="none" className="stroke-primary" strokeWidth="4" />
      </>
    ),
    front: (
      <>
        <rect x="72" y="104" width="34" height="46" rx="5" className={NAVY} />
        <rect x="76" y="108" width="26" height="36" rx="2" className={PALE} />
        <path d="M80 116h18M80 122h14M80 128h18" className="stroke-primary" strokeWidth="2" />
      </>
    ),
  },
  siteManager: {
    skin: SKIN[0], shirt: WHITE, pants: NAVY, head: "helmet", headClass: WHITE,
    over: (
      <>
        <path d="M42 92q0-16 16-16h4v74H42zM98 92q0-16-16-16h-4v74h20z" className={BLUE} />
        <path d="M66 78l4 30 4-30z" className={NAVY} />
      </>
    ),
    back: <rect x="8" y="104" width="48" height="13" rx="6.5" className={LIGHT} transform="rotate(-18 32 110)" />,
    face: (
      <>
        <rect x="56" y="52" width="12" height="9" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <rect x="72" y="52" width="12" height="9" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M68 56h4M63 68q7 3 14 0" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      </>
    ),
  },
  owner: {
    skin: SKIN[1], shirt: BLUE_DARK, pants: NAVY, head: "hair", headClass: STEEL,
    over: (
      <>
        <path d="M58 76h24l-12 34z" className={WHITE} />
        <path d="M58 76l12 34-8 6-10-36zM82 76l-12 34 8 6 10-36z" className={NAVY} opacity="0.35" />
        <rect x="80" y="122" width="12" height="4" rx="2" className={WHITE} />
      </>
    ),
    front: (
      <>
        <rect x="26" y="118" width="20" height="32" rx="4" className={NAVY} />
        <rect x="29" y="122" width="14" height="22" rx="2" className={LIGHT} />
        <circle cx="36" cy="142" r="8" fill={SKIN[1]} />
      </>
    ),
  },
  tiler: {
    skin: SKIN[0], shirt: BLUE, pants: NAVY, head: "capBack", headClass: NAVY,
    back: (
      <>
        <rect x="4" y="104" width="40" height="40" rx="3" className={PALE} stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
        <path d="M24 104v40M4 124h40" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      </>
    ),
    front: (
      <>
        <path d="M106 136l22-4 2 14-22 4z" className={STEEL} />
        <rect x="100" y="126" width="7" height="12" rx="2" className={NAVY} />
        <rect x="47" y="166" width="23" height="16" rx="6" className={BLUE} />
        <rect x="70" y="166" width="23" height="16" rx="6" className={BLUE} />
      </>
    ),
  },
  joiner: {
    skin: SKIN[0], shirt: LIGHT, pants: NAVY, head: "hair", headClass: NAVY,
    over: (
      <>
        <path d="M48 92h44v60H48z" className={NAVY} />
        <path d="M52 92l-4-16M88 92l4-16" className="stroke-foreground" strokeWidth="4" />
        <rect x="58" y="110" width="24" height="14" rx="3" className={BLUE} />
      </>
    ),
    front: <rect x="84" y="112" width="54" height="12" rx="2" className={PALE} stroke="currentColor" strokeOpacity="0.25" />,
  },
  facade: {
    skin: SKIN[2], shirt: BLUE_DARK, pants: NAVY, head: "helmet", headClass: BLUE,
    front: (
      <>
        <rect x="22" y="104" width="96" height="50" rx="3" className={WHITE} stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <g className="fill-primary/30">
          <circle cx="34" cy="116" r="2" /><circle cx="52" cy="128" r="2" /><circle cx="70" cy="114" r="2" />
          <circle cx="88" cy="132" r="2" /><circle cx="106" cy="118" r="2" /><circle cx="78" cy="146" r="2" />
        </g>
      </>
    ),
  },
  digger: {
    skin: SKIN[1], shirt: NAVY, pants: NAVY, head: "helmet", headClass: BLUE_DARK,
    over: vest,
    back: (
      <>
        <rect x="108" y="40" width="7" height="150" rx="3" className={NAVY} />
        <path d="M100 186h24l-3 30q-9 6-18 0z" className={STEEL} />
      </>
    ),
    face: (
      <>
        <rect x="42" y="50" width="10" height="16" rx="5" className={NAVY} />
        <rect x="88" y="50" width="10" height="16" rx="5" className={NAVY} />
        <circle cx="62" cy="57" r="2.6" className={NAVY} />
        <circle cx="78" cy="57" r="2.6" className={NAVY} />
        <path d="M63 67q7 3 14 0" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      </>
    ),
  },
  developer: {
    skin: SKIN[0], shirt: NAVY, pants: NAVY, head: "hair", headClass: NAVY,
    over: (
      <>
        <path d="M60 76h20l-10 30z" className={WHITE} />
        <path d="M68 80h4l3 24-5 6-5-6z" className={BLUE} />
      </>
    ),
    front: (
      <g transform="rotate(-24 112 112)">
        <rect x="84" y="104" width="54" height="15" rx="7.5" className={LIGHT} stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
        <ellipse cx="136" cy="111.5" rx="4" ry="7.5" className={WHITE} stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      </g>
    ),
  },
};

export function CrewFigure({ crew }: { crew: CrewKey }) {
  return <Figure spec={SPECS[crew]} />;
}
